import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { WorkflowDto } from 'model/workflow';
import { denormalizeWorkflow, normalizeWorkflow } from 'model/workflow';
import { ErrorMessage } from 'components/common/ErrorMessage';
import { useFocusTrap } from 'hooks/useFocusTrap';
import { parseJson } from 'utils/parseJson';
import Styled from './RawJsonEditorModal.styled';

const {
  StyledBackdrop,
  StyledDialog,
  StyledHeader,
  StyledTitle,
  StyledBody,
  StyledTextarea,
  StyledFooter,
  StyledJsonErrorSlot,
} = Styled;

interface OwnProps {
  workflow: WorkflowDto;
  onSave: (workflow: WorkflowDto) => void;
  onClose: () => void;
}

type NormalizeInput = Parameters<typeof normalizeWorkflow>[0];

const RawJsonEditorModal = ({ workflow, onSave, onClose }: OwnProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [jsonText, setJsonText] = useState(() =>
    JSON.stringify(denormalizeWorkflow(workflow), null, 2),
  );
  const [parseError, setParseError] = useState<string | null>(null);

  useFocusTrap(dialogRef, true);

  useEffect(() => {
    setJsonText(JSON.stringify(denormalizeWorkflow(workflow), null, 2));
    setParseError(null);
  }, [workflow]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonText(e.target.value);
    setParseError(null);
  }, []);

  const handleSave = useCallback(() => {
    const result = parseJson<NormalizeInput>(jsonText);
    if (!result.ok || result.value === null) {
      setParseError(result.error ?? 'Invalid JSON');
      return;
    }
    onSave(normalizeWorkflow(result.value));
  }, [jsonText, onSave]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Tab') {
        e.preventDefault();
        const textarea = e.currentTarget;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newText = `${jsonText.substring(0, start)}  ${jsonText.substring(end)}`;
        setJsonText(newText);
        // Restore cursor position after state update
        requestAnimationFrame(() => {
          textarea.selectionStart = start + 2;
          textarea.selectionEnd = start + 2;
        });
      }
    },
    [jsonText],
  );

  return (
    <StyledBackdrop onClick={handleBackdropClick}>
      <StyledDialog
        ref={dialogRef}
        role='dialog'
        aria-modal='true'
        aria-labelledby='raw-json-title'
      >
        <StyledHeader>
          <StyledTitle id='raw-json-title'>Raw JSON Editor</StyledTitle>
          <button
            type='button'
            onClick={onClose}
            className='btn-ghost btn-sm'
            title='Close (Escape)'
          >
            Close
          </button>
        </StyledHeader>

        <StyledBody>
          <StyledTextarea
            value={jsonText}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            spellCheck={false}
            aria-label='Workflow JSON'
            $hasError={parseError !== null}
          />
          {parseError && (
            <StyledJsonErrorSlot>
              <ErrorMessage monospace>{parseError}</ErrorMessage>
            </StyledJsonErrorSlot>
          )}
        </StyledBody>

        <StyledFooter>
          <button type='button' onClick={onClose} className='btn-secondary btn-sm'>
            Cancel
          </button>
          <button
            type='button'
            onClick={handleSave}
            className='btn-sm'
            title='Apply JSON changes to canvas'
          >
            Apply
          </button>
        </StyledFooter>
      </StyledDialog>
    </StyledBackdrop>
  );
};

const MemoRawJsonEditorModal = React.memo(RawJsonEditorModal);

export { MemoRawJsonEditorModal as RawJsonEditorModal };
export default MemoRawJsonEditorModal;
